import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig.js';
import { redactBase64FromAiPrompt } from '../NewBlogSystem/utilities/htmlAiRequest.js';

const HISTORY_VERSION = 1;
const MAX_MESSAGES = 50;
const MAX_CONTENT_LENGTH = 2400;
const HISTORY_DOC_ID = 'projectsAndSkills';
const ALLOWED_PROJECT_FIELDS = new Set(['title', 'overview', 'skillIds', 'imageDescriptions']);
const ALLOWED_SKILL_FIELDS = new Set(['title', 'description', 'icon']);
const ALLOWED_TASKS = new Set(['recommend', 'edit-projects', 'edit-skills', 'rewrite']);

const text = (value, limit = MAX_CONTENT_LENGTH) => String(
  redactBase64FromAiPrompt(String(value || '')) || ''
).trim().slice(0, limit);

const sanitizeChanges = (changes, allowedFields) => Object.fromEntries(
  Object.entries(changes && typeof changes === 'object' ? changes : {})
    .filter(([key]) => allowedFields.has(key))
    .map(([key, value]) => [key, redactBase64FromAiPrompt(value)])
);

const sanitizeUpdates = (value) => ({
  projectUpdates: (Array.isArray(value?.projectUpdates) ? value.projectUpdates : [])
    .slice(0, 20)
    .map((update) => ({
      id: text(update?.id, 160),
      changes: sanitizeChanges(update?.changes, ALLOWED_PROJECT_FIELDS),
    }))
    .filter((update) => update.id && Object.keys(update.changes).length),
  skillUpdates: (Array.isArray(value?.skillUpdates) ? value.skillUpdates : [])
    .slice(0, 30)
    .map((update) => ({
      id: text(update?.id, 160),
      changes: sanitizeChanges(update?.changes, ALLOWED_SKILL_FIELDS),
    }))
    .filter((update) => update.id && Object.keys(update.changes).length),
});

const sanitizeUpdateContext = (value) => ({
  projects: (Array.isArray(value?.projects) ? value.projects : []).slice(0, 30).map((project) => ({
    id: text(project?.id, 160),
    title: text(project?.title, 180),
    images: (Array.isArray(project?.images) ? project.images : []).slice(0, 30).map((image) => ({
      id: text(image?.id ?? image?.imageId, 160),
      title: text(image?.title ?? image?.name, 120),
    })),
  })).filter((project) => project.id),
  skills: (Array.isArray(value?.skills) ? value.skills : []).slice(0, 60).map((skill) => ({
    id: text(skill?.id, 160),
    name: text(skill?.name, 120),
  })).filter((skill) => skill.id),
});

const sanitizeDraftProposal = (value) => {
  if (!value || !['project', 'skill'].includes(value.kind)) return null;
  if (value.kind === 'skill') {
    return {
      kind: 'skill',
      name: text(value.name, 180),
      description: text(value.description, 3000),
    };
  }
  return {
    kind: 'project',
    title: text(value.title, 240),
    overview: text(value.overview, 6000),
    images: (Array.isArray(value.images) ? value.images : []).slice(0, 30).map((image) => ({
      imageId: text(image?.imageId, 160),
      description: text(image?.description, 160),
    })).filter((image) => image.imageId),
  };
};

const sanitizeMessage = (message = {}, restored = false) => {
  if (!['user', 'assistant', 'error'].includes(message.role)) return null;
  const content = text(message.content);
  const updates = message.updates ? sanitizeUpdates(message.updates) : null;
  const draftProposal = sanitizeDraftProposal(message.draftProposal);
  const hasUpdates = updates && (updates.projectUpdates.length || updates.skillUpdates.length);
  if (!content && !hasUpdates && !draftProposal) return null;
  return {
    id: text(message.id, 120) || `saved-message-${Date.now()}`,
    role: message.role,
    task: message.task === 'edit'
      ? 'edit-projects'
      : ALLOWED_TASKS.has(message.task) ? message.task : 'recommend',
    content,
    ...(hasUpdates ? { updates } : {}),
    ...(hasUpdates ? { updateContext: sanitizeUpdateContext(message.updateContext) } : {}),
    ...(hasUpdates ? {
      updateStatus: ['applied', 'discarded'].includes(message.updateStatus)
        ? message.updateStatus
        : 'proposed',
    } : {}),
    ...(hasUpdates && ['projects', 'skills'].includes(message.updateScope)
      ? { updateScope: message.updateScope }
      : {}),
    ...(draftProposal ? {
      draftProposal,
      draftStatus: ['applied', 'discarded'].includes(message.draftStatus)
        ? message.draftStatus
        : 'proposed',
    } : {}),
    historical: restored || Boolean(message.historical),
    createdAt: text(message.createdAt, 80) || new Date().toISOString(),
  };
};

export const sanitizeProjectsSkillsAiHistory = (messages = [], restored = false) => (
  (Array.isArray(messages) ? messages : [])
    .map((message) => sanitizeMessage(message, restored))
    .filter(Boolean)
    .slice(-MAX_MESSAGES)
);

export const loadProjectsSkillsAiHistory = async (userId) => {
  if (!userId) return [];
  const snapshot = await getDoc(doc(db, 'users', userId, 'aiConversations', HISTORY_DOC_ID));
  return snapshot.exists()
    ? sanitizeProjectsSkillsAiHistory(snapshot.data()?.messages, true)
    : [];
};

export const saveProjectsSkillsAiHistory = async (userId, messages) => {
  if (!userId) return;
  await setDoc(doc(db, 'users', userId, 'aiConversations', HISTORY_DOC_ID), {
    version: HISTORY_VERSION,
    messages: sanitizeProjectsSkillsAiHistory(messages),
    updatedAt: new Date(),
  }, { merge: true });
};

export { HISTORY_VERSION, MAX_MESSAGES };
